import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird';
import { TypedMemo } from '../../../common/types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useKitWorkBadge } from '../../navigation/hooks/useKitWorkBadge';
import { workBadgeMessages } from '../../navigation/workBadgeMessages';
import { useCleanCopiesNavLabel } from '../../watermarkUpsell/cleanCopies/useCleanCopiesNavLabel';
import { studioWorkPanelMessages as messages } from './studioWorkPanelMessages';
import styles from './StudioWorkChip.module.scss';

interface StudioWorkChipProps {
  onOpen: () => void;
  /** True while the Work panel is open: the chip looks pressed. */
  isOpen?: boolean;
}

/**
 * "2 working · 1 failed" on Studio (design navigation-v2 c 3e). Work left the menu: the chip shows
 * while something is queued, made or failed, and opens the Work panel. It goes away when nothing
 * runs and nothing failed. While a clean-copies run works, the working part reads "Clean copies
 * 9 / 14" (design trial-upsell b5).
 */
const StudioWorkChipComponent: React.FC<StudioWorkChipProps> = ({ onOpen, isOpen = false }) => {
  const intl = useIntl();
  const accountId = useAccountId();
  const work = useKitWorkBadge(accountId);
  const working = work?.working ?? 0;
  const failed = work?.failed ?? 0;
  const cleanCopiesLabel = useCleanCopiesNavLabel(working > 0);
  if (working === 0 && failed === 0) {
    return null;
  }

  const workingText =
    working > 0 ? (cleanCopiesLabel ?? intl.formatMessage(workBadgeMessages.working, { count: working })) : null;
  const failedText =
    failed > 0 ? (
      <span className={styles.bad}>{intl.formatMessage(workBadgeMessages.failed, { count: failed })}</span>
    ) : null;

  return (
    <button
      type="button"
      className={clsx(styles.chip, isOpen && styles.chipOpen)}
      onClick={onOpen}
      aria-expanded={isOpen}
      title={intl.formatMessage(messages.chipHint)}
      data-testid="studio-work-chip"
    >
      {working > 0 && <FontAwesomeIcon icon={faSpinnerThird} spin className={styles.spin} />}
      {workingText !== null && failedText !== null
        ? intl.formatMessage(messages.chipSeparator, { working: workingText, failed: failedText })
        : (workingText ?? failedText)}
    </button>
  );
};

export const StudioWorkChip = TypedMemo(StudioWorkChipComponent);
